import { useEffect, useRef, useState } from "react";
import { BEYOND, ILLUSTRATIVE_PLAN, JOIN, JOIN_LABEL, LOGIN_LABEL, LOOK_INSIDE, PLATFORM_LOGO, PRACTICE, SCENE, STATUS_LABELS, WELCOME } from "../../portalContent";
import { useReveal } from "../../hooks/usePortal";
import JoinForm from "./JoinForm";
import RoomScene from "./RoomScene";

const scrollToJoin = (event) => {
  const target = document.getElementById("join");
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Showcase({ wordmark, name, attribution }) {
  const reveal = useReveal();
  const hero = useRef(null);
  const [pastHero, setPastHero] = useState(false);
  const [joinVisible, setJoinVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > window.innerHeight * 0.7);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const node = document.getElementById("join");
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([entry]) => setJoinVisible(entry.isIntersecting), { threshold: 0.25 });
    io.observe(node);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (window.location.hash !== "#join") return undefined;
    const frame = window.requestAnimationFrame(() => document.getElementById("join")?.scrollIntoView());
    return () => window.cancelAnimationFrame(frame);
  }, []);

  // Writes CSS variables directly so following the pointer never re-renders the page.
  const trackPointer = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    hero.current?.style.setProperty("--mx", `${(((event.clientX - rect.left) / rect.width) * 100).toFixed(1)}%`);
    hero.current?.style.setProperty("--my", `${(((event.clientY - rect.top) / rect.height) * 100).toFixed(1)}%`);
  };

  return (
    <div className="pt-showcase">
      <header className="pt-nav">
        {wordmark ? <img className="pt-nav-mark" src={wordmark} alt={name} /> : <span className="pt-nav-name">{name}</span>}
        <nav aria-label="Studio" className="pt-nav-links">
          <a href="#practice">The practice</a>
          <a href="#studio">The studio</a>
          <a href="#inside">A look inside</a>
        </nav>
        <div className="pt-nav-actions">
          <a className="pt-btn pt-btn-ghost" href="/login">{LOGIN_LABEL}</a>
          <a className="pt-btn pt-btn-primary" href="#join" onClick={scrollToJoin}>{JOIN_LABEL}</a>
        </div>
      </header>

      <section ref={hero} className="pt-hero" onPointerMove={trackPointer}>
        <div className="pt-hero-glow" aria-hidden="true" />
        <div className="pt-hero-inner pt-rise is-in">
          <p className="pt-kicker">{WELCOME.kicker}</p>
          <h1 className="pt-display">{WELCOME.title[0]}<br /><em>{WELCOME.title[1]}</em></h1>
          <p className="pt-lede">{WELCOME.body}</p>
          <div className="pt-cta-row">
            <a className="pt-btn pt-btn-primary pt-btn-lg" href="#join" onClick={scrollToJoin}>{JOIN_LABEL}</a>
            <a className="pt-btn pt-btn-ghost pt-btn-lg" href="/login">Member sign in</a>
          </div>
        </div>
        <a className="pt-scroll-cue" href="#practice" aria-label="Scroll to the practice"><span /></a>
      </section>

      <section id="practice" className="pt-section">
        <h2 ref={reveal} className="pt-title pt-rise">{PRACTICE.title}</h2>
        <div className="pt-cards">
          {PRACTICE.items.map((item, i) => (
            <article key={item.id} ref={reveal} className="pt-card pt-rise" style={{ "--d": `${i * 120}ms` }}>
              <span className="pt-card-numeral" aria-hidden="true">{item.numeral}</span>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="studio" className="pt-section pt-section-alt">
        <div ref={reveal} className="pt-rise">
          <p className="pt-kicker">{SCENE.kicker}</p>
          <h2 className="pt-title">{SCENE.title[0]}<br />{SCENE.title[1]}</h2>
        </div>
        <div ref={reveal} className="pt-rise pt-scene-wrap"><RoomScene /></div>
      </section>

      <section className="pt-section">
        <h2 ref={reveal} className="pt-title pt-title-sm pt-rise">{BEYOND.title}</h2>
        <div className="pt-cards pt-cards-2">
          {BEYOND.items.map((item, i) => (
            <article key={item.id} ref={reveal} className="pt-card pt-rise" style={{ "--d": `${i * 120}ms` }}>
              <span className={`pt-chip pt-chip-${item.status}`}>{STATUS_LABELS[item.status]}</span>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="inside" className="pt-section pt-section-alt pt-inside">
        <div ref={reveal} className="pt-rise pt-inside-copy">
          <p className="pt-kicker">{LOOK_INSIDE.kicker}</p>
          <h2 className="pt-title">{LOOK_INSIDE.title}</h2>
          <p className="pt-lede">{LOOK_INSIDE.body}</p>
        </div>
        <div ref={reveal} className="pt-rise pt-plan" role="img" aria-label="Illustrative studio floor plan, available to members">
          <div className="pt-plan-rooms" aria-hidden="true">
            {ILLUSTRATIVE_PLAN.map((room) => (
              <span key={room.id} className={room.hero ? "is-hero" : ""} style={{ left: `${room.left}%`, top: `${room.top}%`, width: `${room.width}%`, height: `${room.height}%` }} />
            ))}
          </div>
          <div className="pt-plan-lock"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="5" y="11" width="14" height="9" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>{LOOK_INSIDE.lock}</div>
        </div>
      </section>

      <section id="join" className="pt-section pt-join">
        <div ref={reveal} className="pt-rise pt-join-copy">
          <p className="pt-kicker">{JOIN.kicker}</p>
          <h2 className="pt-display pt-display-md">{JOIN.title[0]}<br /><em>{JOIN.title[1]}</em></h2>
        </div>
        <div ref={reveal} className="pt-rise"><JoinForm /></div>
      </section>

      <footer className="pt-footer">
        <div className="pt-powered">
          <span>{attribution || "Powered by"}</span>
          <span className="pt-logo-plate"><img src={PLATFORM_LOGO} alt="HACaMS" /></span>
        </div>
        <span className="pt-footer-name">{name}</span>
      </footer>

      <a className={`pt-float-cta${pastHero && !joinVisible ? " is-on" : ""}`} href="#join" onClick={scrollToJoin} tabIndex={pastHero && !joinVisible ? 0 : -1}>{JOIN_LABEL}</a>
    </div>
  );
}
